import { useState, useEffect } from 'react';
import { Play, ArrowRight, Loader } from 'lucide-react';

const API_BASE =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:3001'
    : 'https://backend-gules-eight-26.vercel.app';

// ─── Design tokens ────────────────────────────────────────────
const gold      = '#C8A96E';
const charcoal  = '#1A1916';
const secondary = '#6B6862';
const tertiary  = '#9A9690';
const divider   = '#E8E6E2';

// ─── Shared button ────────────────────────────────────────────
const OutlineBtn = ({ href, onClick, children }) => {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: '10px',
    padding: '13px 28px', fontSize: '0.75rem', fontWeight: 500,
    letterSpacing: '0.18em', textTransform: 'uppercase',
    border: `1px solid ${gold}`, color: gold,
    backgroundColor: 'transparent', cursor: 'pointer',
    transition: 'background-color 0.25s, color 0.25s',
    textDecoration: 'none',
  };
  const hover = { backgroundColor: gold, color: '#fff' };

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={base}
        onMouseEnter={e => Object.assign(e.currentTarget.style, hover)}
        onMouseLeave={e => Object.assign(e.currentTarget.style, { backgroundColor: 'transparent', color: gold })}
      >{children}</a>
    );
  }
  return (
    <button style={base} onClick={onClick}
      onMouseEnter={e => Object.assign(e.currentTarget.style, hover)}
      onMouseLeave={e => Object.assign(e.currentTarget.style, { backgroundColor: 'transparent', color: gold })}
    >{children}</button>
  );
};

// ─── Section label ────────────────────────────────────────────
const Label = ({ children }) => (
  <p style={{ color: gold, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '16px' }}>
    {children}
  </p>
);

// ─── Short centered rule between sections (like contact page) ─
const SectionRule = () => (
  <div style={{ width: '32px', height: '1px', backgroundColor: gold, margin: '0 auto 72px' }} />
);

const MediaPage = ({ language = 'en' }) => {
  const [loading, setLoading]           = useState(true);
  const [loadingMore, setLoadingMore]   = useState(false);
  const [displayCounts, setDisplayCounts] = useState({ shorts: 6, podcasts: 6, longForm: 4 });

  const [allData, setAllData] = useState({
    youtubeVideos: { shorts: [], longForm: [] },
    spotifyEpisodes: { episodes: [] },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [youtubeRes, spotifyRes] = await Promise.allSettled([
          fetch(`${API_BASE}/api/youtube/videos`),
          fetch(`${API_BASE}/api/spotify/episodes?limit=50`),
        ]);

        let youtubeData = { shorts: [], longForm: [] };
        let spotifyData = { episodes: [] };

        if (youtubeRes.status === 'fulfilled' && youtubeRes.value.ok) {
          const j = await youtubeRes.value.json();
          if (j.success) youtubeData = j.data;
        }
        if (spotifyRes.status === 'fulfilled' && spotifyRes.value.ok) {
          const j = await spotifyRes.value.json();
          if (j.success) spotifyData = j.data;
        }

        setAllData({ youtubeVideos: youtubeData, spotifyEpisodes: spotifyData });
      } catch (e) {
        console.error('Error fetching media data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const loadMore = async (type) => {
    setLoadingMore(true);
    try {
      if (type === 'shorts') {
        setDisplayCounts(p => ({ ...p, shorts: p.shorts + 6 }));
      } else if (type === 'podcasts') {
        const newCount = displayCounts.podcasts + 6;
        if (newCount > (allData.spotifyEpisodes.episodes?.length || 0)) {
          const res = await fetch(`${API_BASE}/api/spotify/episodes?limit=20&offset=${allData.spotifyEpisodes.episodes?.length || 0}`);
          if (res.ok) {
            const j = await res.json();
            if (j.success && j.data.episodes.length > 0) {
              setAllData(p => ({ ...p, spotifyEpisodes: { ...p.spotifyEpisodes, episodes: [...(p.spotifyEpisodes.episodes || []), ...j.data.episodes] } }));
            }
          }
        }
        setDisplayCounts(p => ({ ...p, podcasts: newCount }));
      } else if (type === 'longForm') {
        setDisplayCounts(p => ({ ...p, longForm: p.longForm + 4 }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  };

  const hasMore = (type) => {
    if (type === 'shorts')   return displayCounts.shorts   < (allData.youtubeVideos.shorts?.length || 0);
    if (type === 'podcasts') return displayCounts.podcasts < (allData.spotifyEpisodes.episodes?.length || 0);
    if (type === 'longForm') return displayCounts.longForm < (allData.youtubeVideos.longForm?.length || 0);
    return false;
  };

  const remaining = (type) => {
    if (type === 'shorts')   return Math.max(0, (allData.youtubeVideos.shorts?.length || 0)   - displayCounts.shorts);
    if (type === 'podcasts') return Math.max(0, (allData.spotifyEpisodes.episodes?.length || 0) - displayCounts.podcasts);
    if (type === 'longForm') return Math.max(0, (allData.youtubeVideos.longForm?.length || 0)  - displayCounts.longForm);
    return 0;
  };

  const formatDate = (str) => new Date(str).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { year: 'numeric', month: 'long' });

  const podcasts = allData.spotifyEpisodes.episodes?.slice(0, displayCounts.podcasts) || [];
  const shorts   = allData.youtubeVideos.shorts?.slice(0, displayCounts.shorts) || [];
  const longForm = allData.youtubeVideos.longForm?.slice(0, displayCounts.longForm) || [];

  // Featured conversations — static, high-credibility
  const featured = [
    {
      guest: 'Paul Rabinovitch',
      title: language === 'es' ? 'Capital, Sostenibilidad y Pensamiento a Largo Plazo' : 'Capital, Sustainability, and Long-Term Thinking',
      description: language === 'es' ? 'Una conversación sobre ESG, asignación de capital y construir con impacto a largo plazo.' : 'A conversation on ESG, capital allocation, and building with long-term impact in mind.',
    },
    {
      guest: 'Germán Walas',
      title: language === 'es' ? 'Desarrollo Global y Mentalidad de Operador' : 'Global Development and Operator Mindset',
      description: language === 'es' ? 'Perspectivas sobre desarrollo internacional, ejecución y construcción en múltiples mercados.' : 'Insights on international development, execution, and building across markets.',
    },
    {
      guest: 'Ajay Nwosu',
      title: language === 'es' ? 'Ejecución, Liderazgo y Construir a Escala' : 'Execution, Leadership, and Building at Scale',
      description: language === 'es' ? 'Una discusión sobre mentalidad de fundador, toma de decisiones y estrategia a largo plazo.' : 'A discussion on founder mindset, decision-making, and long-term strategy.',
    },
  ];

  const explore = language === 'es' ? [
    { title: 'Desarrollo Inmobiliario', desc: 'Desde permisos hasta ejecución — cómo se construyen los proyectos.' },
    { title: 'Estrategia de Capital', desc: 'Cómo piensan los inversores, estructuran acuerdos y gestionan el riesgo.' },
    { title: 'Mentalidad de Operador', desc: 'Cómo los fundadores y líderes toman decisiones bajo incertidumbre.' },
    { title: 'IA e Innovación', desc: 'Cómo la tecnología está remodelando el entorno construido.' },
    { title: 'Perspectiva Global', desc: 'Perspectivas en EE.UU., América Latina y mercados internacionales.' },
  ] : [
    { title: 'Real Estate Development', desc: 'From entitlements to execution — how projects are actually built.' },
    { title: 'Capital Strategy', desc: 'How investors think, structure deals, and manage risk.' },
    { title: 'Operator Mindset', desc: 'How founders and leaders make decisions under uncertainty.' },
    { title: 'AI & Innovation', desc: 'How technology is reshaping the built environment.' },
    { title: 'Global Perspective', desc: 'Insights across the U.S., Latin America, and international markets.' },
  ];

  const shorts_topics = language === 'es'
    ? ['IRR / TIR explicado', 'Estructuras de capital mixto', 'IA en desarrollo', 'Construcción industrializada']
    : ['IRR / TIR explained', 'Blended capital stacks', 'AI in development', 'Industrialized construction'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: gold }} />
          <p style={{ color: tertiary, fontSize: '0.85rem', letterSpacing: '0.05em' }}>
            {language === 'es' ? 'Cargando...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div style={{ paddingTop: 'env(safe-area-inset-top)', position: 'sticky', top: 0, zIndex: 50, backgroundColor: '#fff' }} />

      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section style={{ backgroundColor: '#fff', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <div style={{ maxWidth: '680px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
          <Label>{language === 'es' ? 'Plataforma de Medios' : 'Media Platform'}</Label>
          <h1 style={{ color: charcoal, fontSize: 'clamp(2.8rem, 6vw, 5rem)', fontWeight: 300, lineHeight: 1.05, letterSpacing: '-0.025em', marginBottom: '20px' }}>
            {language === 'es' ? 'Medios' : 'Media'}
          </h1>
          <p style={{ color: secondary, fontSize: '1.05rem', fontWeight: 300, lineHeight: 1.8, marginBottom: '16px' }}>
            {language === 'es'
              ? 'Conversaciones en la intersección de bienes raíces, capital y pensamiento de largo horizonte.'
              : 'Conversations at the intersection of real estate, capital, and long-horizon thinking.'}
          </p>
          <div style={{ width: '32px', height: '1px', backgroundColor: gold, margin: '28px auto 28px' }} />
          <p style={{ color: tertiary, fontSize: '0.88rem', fontWeight: 300, lineHeight: 2, maxWidth: '520px', margin: '0 auto' }}>
            {language === 'es'
              ? 'Esta plataforma documenta conversaciones con desarrolladores, inversores y operadores que están dando forma al futuro del entorno construido. En discusiones de largo y corto formato, el objetivo no es contenido — es claridad.'
              : 'This platform documents conversations with developers, investors, and operators shaping the future of the built environment. Across long-form discussions and short-form insights, the goal is not content — it is clarity.'}
          </p>
        </div>
        <div className="scroll-arrow" style={{ position: 'absolute', bottom: '40px', left: '50%', transform: 'translateX(-50%)' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3v14M3 10l7 7 7-7" stroke={gold} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </section>

      {/* ─── SECTION 1 — THE INFINITY PODCAST ────────────────── */}
      <section style={{ backgroundColor: '#fff', padding: '48px 24px 80px' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <Label>{language === 'es' ? 'El Podcast' : 'The Podcast'}</Label>
            <h2 style={{ color: charcoal, fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 300, lineHeight: 1.2, marginBottom: '20px' }}>
              {language === 'es' ? 'The Infinity Podcast' : 'The Infinity Podcast'}
            </h2>
            <p style={{ color: secondary, fontSize: '0.9rem', fontWeight: 300, lineHeight: 1.9, marginBottom: '12px' }}>
              {language === 'es'
                ? 'Una plataforma bilingüe con conversaciones con desarrolladores, inversores, fundadores y operadores en bienes raíces, capital e innovación.'
                : 'A bilingual platform featuring conversations with developers, investors, founders, and operators working across real estate, capital, and innovation.'}
            </p>
            <p style={{ color: tertiary, fontSize: '0.85rem', fontWeight: 300, lineHeight: 2, marginBottom: '32px' }}>
              {language === 'es'
                ? 'Disponible en YouTube, Spotify, Apple Podcasts y todas las plataformas principales.'
                : 'Available on YouTube, Spotify, Apple Podcasts, and all major platforms.'}
            </p>

            {/* What each episode focuses on */}
            <div style={{ textAlign: 'left', maxWidth: '420px', margin: '0 auto 40px', borderLeft: `1px solid ${divider}`, paddingLeft: '20px' }}>
              {(language === 'es' ? [
                'cómo piensan los operadores',
                'cómo se estructura el capital',
                'cómo se construyen las plataformas',
                'cómo se crea valor a largo plazo',
              ] : [
                'how operators think',
                'how capital is structured',
                'how platforms are built',
                'and how long-term value is created',
              ]).map((item, i) => (
                <p key={i} style={{ color: secondary, fontSize: '0.85rem', fontWeight: 300, lineHeight: 2 }}>— {item}</p>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <OutlineBtn href="https://www.youtube.com/@thezeushernandez">
                {language === 'es' ? 'Ver Episodios' : 'Watch Episodes'}
              </OutlineBtn>
              <OutlineBtn href="https://open.spotify.com/show/3y5E9EcqKNPonl1YoEzg8O?si=b16e5a6afe6d4add">
                {language === 'es' ? 'Escuchar Ahora' : 'Listen Now'}
              </OutlineBtn>
            </div>
          </div>

          {/* Spotify embed */}
          <div style={{ borderRadius: '4px', overflow: 'hidden', border: `1px solid ${divider}` }}>
            <iframe
              src="https://open.spotify.com/embed/show/3y5E9EcqKNPonl1YoEzg8O?utm_source=generator&theme=0"
              width="100%" height="232"
              style={{ border: 'none' }}
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              title="The Infinity Podcast on Spotify"
            />
          </div>
        </div>
      </section>

      <SectionRule />
      {/* ─── SECTION 2 — FEATURED CONVERSATIONS (white) ───────── */}
      <section style={{ backgroundColor: '#fff', padding: '80px 24px' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <Label>{language === 'es' ? 'Conversaciones Destacadas' : 'Featured Conversations'}</Label>
            <h2 style={{ color: charcoal, fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 300, lineHeight: 1.2 }}>
              {language === 'es' ? 'Conversaciones Destacadas' : 'Featured Conversations'}
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {featured.map((c, i) => (
              <div key={i} style={{ backgroundColor: '#fff', padding: '36px 0', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: '24px', borderBottom: i < featured.length - 1 ? `1px solid ${divider}` : 'none' }}>
                <div>
                  <p style={{ color: gold, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '8px' }}>
                    {language === 'es' ? 'Con' : 'With'} {c.guest}
                  </p>
                  <h3 style={{ color: charcoal, fontSize: '1.05rem', fontWeight: 400, lineHeight: 1.5, marginBottom: '8px' }}>{c.title}</h3>
                  <p style={{ color: secondary, fontSize: '0.85rem', fontWeight: 300, lineHeight: 1.8 }}>{c.description}</p>
                </div>
                <a
                  href="https://www.youtube.com/@thezeushernandez"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ flexShrink: 0, width: '40px', height: '40px', border: `1px solid #E5E3DE`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: secondary, textDecoration: 'none', transition: 'border-color 0.2s, color 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = gold; e.currentTarget.style.color = gold; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E3DE'; e.currentTarget.style.color = secondary; }}
                  aria-label={`Watch episode with ${c.guest}`}
                >
                  <Play style={{ width: '14px', height: '14px', marginLeft: '2px' }} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionRule />
      {/* ─── SECTION 3 — WHAT WE EXPLORE ─────────────────────── */}
      <section style={{ backgroundColor: '#fff', padding: '80px 24px' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <Label>{language === 'es' ? 'Temas' : 'Topics'}</Label>
            <h2 style={{ color: charcoal, fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 300, lineHeight: 1.2 }}>
              {language === 'es' ? 'Lo Que Exploramos' : 'What We Explore'}
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {explore.map((item, i) => (
              <div key={i} style={{ backgroundColor: '#fff', padding: '28px 0', borderBottom: `1px solid ${divider}` }}>
                <div style={{ width: '6px', height: '1px', backgroundColor: gold, marginBottom: '14px' }} />
                <h3 style={{ color: charcoal, fontSize: '0.95rem', fontWeight: 400, marginBottom: '8px' }}>{item.title}</h3>
                <p style={{ color: secondary, fontSize: '0.82rem', fontWeight: 300, lineHeight: 1.85 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionRule />
      {/* ─── SECTION 4 — SHORTS & VISUAL INSIGHTS (white) ────── */}
      <section style={{ backgroundColor: '#fff', padding: '80px 24px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <Label>{language === 'es' ? 'Formato Corto' : 'Short-form'}</Label>
            <h2 style={{ color: charcoal, fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 300, lineHeight: 1.2, marginBottom: '16px' }}>
              {language === 'es' ? 'Shorts e Información Visual' : 'Shorts & Visual Insights'}
            </h2>
            <p style={{ color: secondary, fontSize: '0.9rem', fontWeight: 300, lineHeight: 1.9, maxWidth: '480px', margin: '0 auto 24px' }}>
              {language === 'es'
                ? 'Contenido de formato corto diseñado para destilar ideas complejas en marcos claros y accionables.'
                : 'Short-form content designed to break down complex ideas into clear, actionable frameworks.'}
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '40px' }}>
              {shorts_topics.map((t, i) => (
                <span key={i} style={{ fontSize: '0.72rem', fontWeight: 400, letterSpacing: '0.1em', color: secondary, border: '1px solid #E5E3DE', padding: '5px 12px' }}>
                  {t}
                </span>
              ))}
            </div>
            <p style={{ color: tertiary, fontSize: '0.82rem', fontWeight: 300, fontStyle: 'italic', letterSpacing: '0.02em', marginBottom: '40px' }}>
              {language === 'es' ? 'No son destacados — son pensamiento destilado.' : 'These are not highlights — they are distilled thinking.'}
            </p>
          </div>

          {/* Shorts grid */}
          {shorts.length > 0 ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px', marginBottom: '32px' }}>
                {shorts.map((video, i) => (
                  <div
                    key={video.id || i}
                    onClick={() => window.open(`https://www.youtube.com/watch?v=${video.id}`, '_blank')}
                    style={{ cursor: 'pointer', position: 'relative', aspectRatio: '9/16', backgroundColor: '#111', overflow: 'hidden' }}
                    onMouseEnter={e => { e.currentTarget.querySelector('.play-overlay').style.opacity = '1'; }}
                    onMouseLeave={e => { e.currentTarget.querySelector('.play-overlay').style.opacity = '0'; }}
                  >
                    <img
                      src={video.thumbnail || `https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                      alt={video.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={e => { e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`; }}
                    />
                    <div className="play-overlay" style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}>
                      <div style={{ width: '36px', height: '36px', border: `1px solid ${gold}`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Play style={{ width: '12px', height: '12px', color: gold, marginLeft: '2px' }} />
                      </div>
                    </div>
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', padding: '16px 8px 8px' }}>
                      <p style={{ color: '#fff', fontSize: '0.7rem', fontWeight: 300, lineHeight: 1.4, margin: 0 }} className="line-clamp-2">{video.title}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {hasMore('shorts') && (
                  <OutlineBtn onClick={() => loadMore('shorts')}>
                    {loadingMore ? <Loader style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} /> : null}
                    {language === 'es' ? `Cargar más (${remaining('shorts')})` : `Load more (${remaining('shorts')})`}
                  </OutlineBtn>
                )}
                <OutlineBtn href="https://www.youtube.com/@thezeushernandez">
                  {language === 'es' ? 'Ver en YouTube' : 'Watch Shorts'}
                </OutlineBtn>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <OutlineBtn href="https://www.youtube.com/@thezeushernandez">
                {language === 'es' ? 'Ver Shorts en YouTube' : 'Watch Shorts on YouTube'}
              </OutlineBtn>
            </div>
          )}
        </div>
      </section>

      {longForm.length > 0 && <SectionRule />}
      {/* ─── SECTION 4b — LONG-FORM VIDEOS (white, continued) ── */}
      {longForm.length > 0 && (
        <section style={{ backgroundColor: '#fff', padding: '64px 24px' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ marginBottom: '40px' }}>
              <Label>{language === 'es' ? 'Formato Largo' : 'Long-form'}</Label>
              <h2 style={{ color: charcoal, fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: 300, lineHeight: 1.2 }}>
                {language === 'es' ? 'Conversaciones en Profundidad' : 'In-Depth Conversations'}
              </h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '24px', marginBottom: '32px' }}>
              {longForm.map((video, i) => (
                <div
                  key={video.id || i}
                  onClick={() => window.open(`https://www.youtube.com/watch?v=${video.id}`, '_blank')}
                  style={{ cursor: 'pointer' }}
                >
                  <div style={{ position: 'relative', aspectRatio: '16/9', backgroundColor: '#111', overflow: 'hidden', marginBottom: '12px' }}
                    onMouseEnter={e => { e.currentTarget.querySelector('.lf-overlay').style.opacity = '1'; }}
                    onMouseLeave={e => { e.currentTarget.querySelector('.lf-overlay').style.opacity = '0'; }}
                  >
                    <img
                      src={video.thumbnail || `https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                      alt={video.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => { e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`; }}
                    />
                    <div className="lf-overlay" style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}>
                      <div style={{ width: '44px', height: '44px', border: `1px solid ${gold}`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Play style={{ width: '14px', height: '14px', color: gold, marginLeft: '2px' }} />
                      </div>
                    </div>
                    {video.duration && (
                      <div style={{ position: 'absolute', bottom: '8px', right: '8px', backgroundColor: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: '0.7rem', padding: '2px 6px' }}>
                        {video.duration}
                      </div>
                    )}
                  </div>
                  <h3 style={{ color: charcoal, fontSize: '0.9rem', fontWeight: 400, lineHeight: 1.5, marginBottom: '6px' }} className="line-clamp-2">{video.title}</h3>
                  {video.publishedAt && <p style={{ color: tertiary, fontSize: '0.75rem', fontWeight: 300 }}>{formatDate(video.publishedAt)}</p>}
                </div>
              ))}
            </div>

            {hasMore('longForm') && (
              <div style={{ textAlign: 'center' }}>
                <OutlineBtn onClick={() => loadMore('longForm')}>
                  {loadingMore ? <Loader style={{ width: '14px', height: '14px' }} /> : null}
                  {language === 'es' ? `Cargar más (${remaining('longForm')})` : `Load more (${remaining('longForm')})`}
                  <ArrowRight style={{ width: '14px', height: '14px' }} />
                </OutlineBtn>
              </div>
            )}
          </div>
        </section>
      )}

      {podcasts.length > 0 && <SectionRule />}
      {/* ─── PODCAST EPISODES (white) ─────────────────────────── */}
      {podcasts.length > 0 && (
        <section style={{ backgroundColor: '#fff', padding: '64px 24px' }}>
          <div style={{ maxWidth: '720px', margin: '0 auto' }}>
            <div style={{ marginBottom: '40px' }}>
              <Label>{language === 'es' ? 'Episodios Recientes' : 'Recent Episodes'}</Label>
              <h2 style={{ color: charcoal, fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: 300, lineHeight: 1.2 }}>
                {language === 'es' ? 'Episodios del Podcast' : 'Podcast Episodes'}
              </h2>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {podcasts.map((ep, i) => (
                <div
                  key={ep.id || i}
                  onClick={() => ep.spotifyUrl && window.open(ep.spotifyUrl, '_blank')}
                  style={{ cursor: 'pointer', padding: '24px 0', borderBottom: `1px solid ${divider}`, display: 'grid', gridTemplateColumns: '1fr auto', gap: '16px', alignItems: 'center' }}
                  onMouseEnter={e => { e.currentTarget.querySelector('h3').style.color = gold; }}
                  onMouseLeave={e => { e.currentTarget.querySelector('h3').style.color = charcoal; }}
                >
                  <div>
                    <p style={{ color: tertiary, fontSize: '0.72rem', fontWeight: 300, letterSpacing: '0.08em', marginBottom: '6px' }}>
                      {ep.publishedAt ? formatDate(ep.publishedAt) : ''}{ep.duration ? ` · ${ep.duration}` : ''}
                    </p>
                    <h3 style={{ color: charcoal, fontSize: '0.95rem', fontWeight: 400, lineHeight: 1.5, marginBottom: '6px', transition: 'color 0.2s' }} className="line-clamp-2">
                      {ep.name}
                    </h3>
                    <p style={{ color: secondary, fontSize: '0.8rem', fontWeight: 300, lineHeight: 1.7 }} className="line-clamp-2">
                      {ep.description?.replace(/<[^>]*>/g, '').slice(0, 120)}...
                    </p>
                  </div>
                  <div style={{ width: '36px', height: '36px', border: `1px solid #E5E3DE`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: secondary }}>
                    <Play style={{ width: '12px', height: '12px', marginLeft: '2px' }} />
                  </div>
                </div>
              ))}
            </div>

            {hasMore('podcasts') && (
              <div style={{ textAlign: 'center', marginTop: '32px' }}>
                <OutlineBtn onClick={() => loadMore('podcasts')}>
                  {loadingMore ? <Loader style={{ width: '14px', height: '14px' }} /> : null}
                  {language === 'es' ? `Más episodios (${remaining('podcasts')})` : `More episodes (${remaining('podcasts')})`}
                  <ArrowRight style={{ width: '14px', height: '14px' }} />
                </OutlineBtn>
              </div>
            )}
          </div>
        </section>
      )}

      <SectionRule />
      {/* ─── SECTION 5 — APPEARANCES ──────────────────────────── */}
      <section style={{ backgroundColor: '#fff', padding: '80px 24px' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <Label>{language === 'es' ? 'Apariciones' : 'Appearances'}</Label>
            <h2 style={{ color: charcoal, fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 300, lineHeight: 1.2, marginBottom: '16px' }}>
              {language === 'es' ? 'Apariciones y Colaboraciones' : 'Appearances & Collaborations'}
            </h2>
            <p style={{ color: secondary, fontSize: '0.9rem', fontWeight: 300, lineHeight: 1.9, maxWidth: '480px', margin: '0 auto' }}>
              {language === 'es'
                ? 'Conversaciones, paneles y colaboraciones con plataformas en bienes raíces, venture e innovación.'
                : 'Conversations, panels, and collaborations with platforms and organizations across real estate, venture, and innovation.'}
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {(language === 'es' ? [
              'Apariciones como invitado en podcasts externos',
              'Paneles y eventos de la industria',
              'Conversaciones institucionales',
            ] : [
              'Guest appearances on external podcasts',
              'Industry panels / events',
              'Institutional conversations',
            ]).map((item, i) => (
              <div key={i} style={{ backgroundColor: '#fff', padding: '20px 0', borderBottom: `1px solid ${divider}`, display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: gold, flexShrink: 0 }} />
                <p style={{ color: secondary, fontSize: '0.88rem', fontWeight: 300, lineHeight: 1.8 }}>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionRule />
      {/* ─── SECTION 6 — WHAT'S NEXT ──────────────────────────── */}
      <section style={{ backgroundColor: '#fff', padding: '80px 24px' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <Label>{language === 'es' ? 'Lo Que Viene' : "What's Next"}</Label>
            <h2 style={{ color: charcoal, fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 300, lineHeight: 1.2, marginBottom: '20px' }}>
              {language === 'es' ? 'Lo Que Se Viene' : 'Looking Ahead'}
            </h2>
            <p style={{ color: secondary, fontSize: '0.9rem', fontWeight: 300, lineHeight: 1.9, marginBottom: '32px' }}>
              {language === 'es'
                ? 'Las próximas conversaciones se expandirán hacia:'
                : 'Upcoming conversations will expand into:'}
            </p>
          </div>

          <div style={{ borderLeft: `1px solid ${divider}`, paddingLeft: '24px', marginBottom: '40px' }}>
            {(language === 'es' ? [
              'plataformas de desarrollo global',
              'innovación inmobiliaria respaldada por venture',
              'asignación de capital a escala',
              'y el futuro de la infraestructura',
            ] : [
              'global development platforms',
              'venture-backed real estate innovation',
              'capital allocation at scale',
              'and the future of infrastructure',
            ]).map((item, i) => (
              <p key={i} style={{ color: secondary, fontSize: '0.88rem', fontWeight: 300, lineHeight: 2 }}>— {item}</p>
            ))}
          </div>
          <p style={{ color: tertiary, fontSize: '0.82rem', fontWeight: 300, lineHeight: 1.9, textAlign: 'center' }}>
            {language === 'es'
              ? 'Con conversaciones con operadores, fundadores y líderes que están dando forma a industrias más allá de los bienes raíces.'
              : 'Featuring conversations with operators, founders, and leaders shaping industries beyond real estate.'}
          </p>
        </div>
      </section>

      <SectionRule />
      {/* ─── SECTION 7 — WHY THIS EXISTS ──────────────────────── */}
      <section style={{ backgroundColor: '#fff', padding: '80px 24px' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <Label>{language === 'es' ? 'Propósito' : 'Purpose'}</Label>
          <h2 style={{ color: charcoal, fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 300, lineHeight: 1.2, marginBottom: '20px' }}>
            {language === 'es' ? 'Por Qué Existe Esta Plataforma' : 'Why This Platform Exists'}
          </h2>
          <p style={{ color: secondary, fontSize: '0.9rem', fontWeight: 300, lineHeight: 1.9, marginBottom: '16px' }}>
            {language === 'es'
              ? 'La mayoría del contenido en bienes raíces se enfoca en tácticas.'
              : 'Most content in real estate focuses on tactics.'}
          </p>
          <p style={{ color: secondary, fontSize: '0.9rem', fontWeight: 300, lineHeight: 1.9, marginBottom: '8px' }}>
            {language === 'es' ? 'Esta plataforma se enfoca en:' : 'This platform focuses on:'}
          </p>
          <div style={{ display: 'flex', gap: '32px', justifyContent: 'center', margin: '24px 0 32px' }}>
            {(language === 'es' ? ['pensamiento', 'toma de decisiones', 'sistemas'] : ['thinking', 'decision-making', 'systems']).map((w, i) => (
              <p key={i} style={{ color: charcoal, fontSize: '0.88rem', fontWeight: 400, letterSpacing: '0.05em' }}>{w}</p>
            ))}
          </div>
          <div style={{ width: '32px', height: '1px', backgroundColor: gold, margin: '0 auto 32px' }} />
          <p style={{ color: tertiary, fontSize: '0.82rem', fontWeight: 300, lineHeight: 2 }}>
            {language === 'es'
              ? 'El objetivo es construir un cuerpo de conversaciones que se componga con el tiempo — creando un punto de referencia para cómo la próxima generación de desarrolladores e inversores se acerca al entorno construido.'
              : 'The goal is to build a body of conversations that compound over time — creating a reference point for how the next generation of developers and investors approach the built environment.'}
          </p>
        </div>
      </section>

      <SectionRule />
      {/* ─── CTA ──────────────────────────────────────────────── */}
      <section style={{ backgroundColor: '#fff', padding: '80px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: '560px', margin: '0 auto' }}>
          <Label>{language === 'es' ? 'Únete' : 'Join the Conversation'}</Label>
          <h2 style={{ color: charcoal, fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)', fontWeight: 300, lineHeight: 1.3, marginBottom: '16px' }}>
            {language === 'es' ? '¿Buscas Colaborar o Unirte a la Conversación?' : 'Looking to Collaborate or Join the Conversation?'}
          </h2>
          <p style={{ color: secondary, fontSize: '0.88rem', fontWeight: 300, lineHeight: 2, marginBottom: '40px' }}>
            {language === 'es'
              ? 'Ya sea que estés construyendo, invirtiendo o explorando nuevas ideas en el entorno construido — esta plataforma está abierta a conversaciones reflexivas.'
              : 'Whether you are building, investing, or exploring new ideas in the built environment — this platform is open to thoughtful conversations.'}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <OutlineBtn href="#contact">
              {language === 'es' ? 'Ponerse en Contacto' : 'Get in Touch'}
            </OutlineBtn>
            <OutlineBtn href="#contact">
              {language === 'es' ? 'Sugerir un Invitado' : 'Suggest a Guest'}
            </OutlineBtn>
          </div>
        </div>
      </section>

    </div>
  );
};

export default MediaPage;
